import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Bot, Play, Trash2, Plus, Clock, CheckCircle2, XCircle, Loader2, RefreshCw, KeyRound } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", type: "dear_portal", base_url: "", username: "", password: "", enabled: true };

function StatusBadge({ status }) {
  if (status === "running") return <span className="inline-flex items-center gap-1 text-amber-600 text-xs font-semibold"><Loader2 size={13} className="animate-spin" /> Running…</span>;
  if (status === "success") return <span className="inline-flex items-center gap-1 text-emerald-600 text-xs font-semibold"><CheckCircle2 size={13} /> Success</span>;
  if (status === "error") return <span className="inline-flex items-center gap-1 text-red-600 text-xs font-semibold"><XCircle size={13} /> Failed</span>;
  return <span className="text-xs text-muted-foreground">Never run</span>;
}

export default function ScraperTab() {
  const [sources, setSources] = useState(null);
  const [runs, setRuns] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);

  const saveLogin = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/scraper-sources/${editing.id}`, { username: editing.username, password: editing.password });
      toast.success("Login updated"); setEditing(null); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update"); }
    finally { setBusy(false); }
  };

  const load = useCallback(async () => {
    const [s, r] = await Promise.all([
      api.get("/admin/scraper-sources"),
      api.get("/admin/scraper-runs"),
    ]);
    setSources(s.data);
    setRuns(r.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Poll while any source is running.
  useEffect(() => {
    if (!sources) return;
    if (!sources.some((s) => s.last_status === "running")) return;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [sources, load]);

  const create = async () => {
    if (!form.name || !form.base_url || !form.username) { toast.error("Name, portal URL and username are required"); return; }
    setBusy(true);
    try {
      await api.post("/admin/scraper-sources", form);
      toast.success("Supplier source added");
      setForm(EMPTY); setAdding(false); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to add"); }
    finally { setBusy(false); }
  };

  const toggle = async (s, v) => {
    setSources((cur) => cur.map((x) => (x.id === s.id ? { ...x, enabled: v } : x)));
    try { await api.put(`/admin/scraper-sources/${s.id}`, { enabled: v }); }
    catch { toast.error("Failed to update"); await load(); }
  };

  const runNow = async (s) => {
    setSources((cur) => cur.map((x) => (x.id === s.id ? { ...x, last_status: "running" } : x)));
    try {
      await api.post(`/admin/scraper-sources/${s.id}/run`);
      toast.success(`Scraping ${s.name}… this runs in the background`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to start"); await load(); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete supplier source "${s.name}"?`)) return;
    try { await api.delete(`/admin/scraper-sources/${s.id}`); toast.success("Deleted"); await load(); }
    catch { toast.error("Failed to delete"); }
  };

  if (!sources) return <p className="text-muted-foreground">Loading…</p>;

  return (
    <div className="max-w-4xl" data-testid="scraper-tab">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-3xl font-extrabold mb-1 flex items-center gap-2"><Bot className="text-accent" /> Supplier Scrapers</h1>
          <p className="text-muted-foreground">Automatically pull stock & pricing from gated B2B supplier portals. Prices are treated as your cost (×markup ×GST) and imported using the same rules as the dealer feed. Runs automatically every day at <strong>7:45 PM NZ</strong>.</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="scraper-refresh"><RefreshCw size={15} className="mr-1.5" /> Refresh</Button>
      </div>

      {sources.length === 0 && !adding && (
        <div className="rounded-xl border border-dashed bg-white p-8 text-center mb-6">
          <Bot className="mx-auto text-muted-foreground mb-2" />
          <p className="text-muted-foreground mb-4">No supplier sources yet.</p>
        </div>
      )}

      <div className="space-y-4 mb-6">
        {sources.map((s) => (
          <div key={s.id} className="rounded-xl border bg-white p-5" data-testid={`scraper-source-${s.id}`}>
            <div className="flex items-center justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h2 className="font-bold text-lg truncate">{s.name}</h2>
                  <StatusBadge status={s.last_status} />
                </div>
                <p className="text-sm text-muted-foreground truncate">{s.base_url} <span className="ml-1 text-xs px-1.5 py-0.5 rounded bg-slate-100" data-testid={`scraper-type-badge-${s.id}`}>{s.type === "woocommerce" ? "WooCommerce" : "DEAR portal"}</span></p>
                <p className="text-xs text-muted-foreground mt-0.5">User: {s.username} · <Clock size={11} className="inline -mt-0.5" /> Daily 7:45 PM NZ</p>
                {s.last_counts && (
                  <p className="text-xs text-slate-600 mt-1">
                    Last run: {s.last_counts.products_found ?? 0} found · {s.last_counts.created ?? 0} new · {s.last_counts.updated ?? 0} updated
                    {s.last_counts.held ? ` · ${s.last_counts.held} held` : ""}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-muted-foreground">Enabled</Label>
                  <Switch data-testid={`scraper-enable-${s.id}`} checked={!!s.enabled} onCheckedChange={(v) => toggle(s, v)} />
                </div>
                <Button size="sm" disabled={s.last_status === "running"} onClick={() => runNow(s)}
                  data-testid={`scraper-run-${s.id}`} className="bg-accent text-steel hover:bg-amber-600 font-semibold">
                  {s.last_status === "running" ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <Play size={15} className="mr-1.5" />} Run Now
                </Button>
                <Button size="sm" variant="outline" onClick={() => setEditing({ id: s.id, username: s.username, password: "" })} data-testid={`scraper-edit-login-${s.id}`}>
                  <KeyRound size={15} className="mr-1.5" /> Edit login
                </Button>
                <Button size="sm" variant="ghost" onClick={() => remove(s)} data-testid={`scraper-delete-${s.id}`} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                  <Trash2 size={15} />
                </Button>
              </div>
            </div>
            {editing?.id === s.id && (
              <div className="mt-4 pt-4 border-t grid sm:grid-cols-[1fr_1fr_auto] gap-3 items-end" data-testid={`scraper-edit-form-${s.id}`}>
                <div><Label>Username</Label><Input data-testid="scraper-edit-username" value={editing.username} onChange={(e) => setEditing({ ...editing, username: e.target.value })} /></div>
                <div><Label>New password</Label><Input data-testid="scraper-edit-password" type="password" value={editing.password} onChange={(e) => setEditing({ ...editing, password: e.target.value })} placeholder="Leave blank to keep current" /></div>
                <div className="flex gap-2">
                  <Button onClick={saveLogin} disabled={busy} data-testid="scraper-edit-save" className="bg-steel text-white">Save</Button>
                  <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {adding ? (
        <div className="rounded-xl border bg-white p-6 mb-6" data-testid="scraper-add-form">
          <h2 className="font-bold text-lg mb-4">Add supplier source</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2"><Label>Portal type</Label>
              <select data-testid="scraper-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="h-10 w-full rounded-md border bg-white px-3 text-sm">
                <option value="dear_portal">Cin7 Core / DEAR B2B portal (e.g. Goodson)</option>
                <option value="woocommerce">WooCommerce trade website (e.g. ITPlus)</option>
              </select></div>
            <div><Label>Name</Label><Input data-testid="scraper-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={form.type === "woocommerce" ? "ITPlus" : "Goodson"} /></div>
            <div><Label>Portal URL</Label><Input data-testid="scraper-url" value={form.base_url} onChange={(e) => setForm({ ...form, base_url: e.target.value })} placeholder={form.type === "woocommerce" ? "https://www.itplus.co.nz" : "https://goodsonnz.dearportal.com"} /></div>
            <div><Label>Username</Label><Input data-testid="scraper-username" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} placeholder="info@acponline.co.nz" /></div>
            <div><Label>Password</Label><Input data-testid="scraper-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Portal password" /></div>
          </div>
          <p className="text-xs text-muted-foreground mt-3">Supported: Cin7 Core / DEAR B2B portals (*.dearportal.com) and WooCommerce trade websites (e.g. itplus.co.nz). Credentials are stored securely and never shown again.</p>
          <div className="flex gap-3 mt-4">
            <Button onClick={create} disabled={busy} data-testid="scraper-save" className="bg-steel text-white hover:bg-steel-light font-semibold">{busy ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : null} Save source</Button>
            <Button variant="outline" onClick={() => { setAdding(false); setForm(EMPTY); }}>Cancel</Button>
          </div>
        </div>
      ) : (
        <Button onClick={() => setAdding(true)} data-testid="scraper-add-btn" className="bg-steel text-white hover:bg-steel-light font-semibold mb-8">
          <Plus size={16} className="mr-1.5" /> Add supplier source
        </Button>
      )}

      {runs.length > 0 && (
        <div>
          <h2 className="font-bold text-lg mb-3">Recent runs</h2>
          <div className="rounded-xl border bg-white overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-muted-foreground">
                <tr><th className="text-left px-4 py-2">Source</th><th className="text-left px-4 py-2">When</th><th className="text-left px-4 py-2">Status</th><th className="text-left px-4 py-2">Result</th></tr>
              </thead>
              <tbody>
                {runs.map((r) => (
                  <tr key={r.id} className="border-t" data-testid={`scraper-run-row-${r.id}`}>
                    <td className="px-4 py-2 font-medium">{r.source_name}</td>
                    <td className="px-4 py-2 text-muted-foreground">{r.started ? new Date(r.started).toLocaleString("en-NZ", { timeZone: "Pacific/Auckland" }) : "—"}</td>
                    <td className="px-4 py-2"><StatusBadge status={r.status} /></td>
                    <td className="px-4 py-2 text-slate-600">
                      {r.error ? <span className="text-red-600">{r.error}</span> :
                        `${r.counts?.products_found ?? 0} found · ${r.counts?.created ?? 0} new · ${r.counts?.updated ?? 0} updated${r.counts?.held ? ` · ${r.counts.held} held` : ""}`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
